import { useCallback } from "react";
import { RowClassParams, GetRowIdParams } from "ag-grid-community";

import { BaseTableData } from "../types";
import { getRowClasses } from "../utils";

interface UseRowHandlersProps<T extends BaseTableData> {
  onRowClick?: (data: T) => void;
  onRowDoubleClick?: (data: T) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectionChanged?: (event: any) => void;
  hoverable: boolean;
  striped?: boolean;
  isHighlighted?: (data: T) => boolean;
  getRowId?: (params: GetRowIdParams<T>) => string;
}

export const useRowHandlers = <T extends BaseTableData>({
  onRowClick,
  onRowDoubleClick,
  onSelectionChanged,
  hoverable,
  striped,
  isHighlighted,
  getRowId,
}: UseRowHandlersProps<T>) => {
  const handleRowClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      const target = event.event.target as HTMLElement;
      const isInteractiveElement =
        target.closest("button") || target.closest("div")?.getAttribute("type") === "button";

      const isDetailRow = Boolean(event.node?.detail);

      if (onRowClick && event.data && !isInteractiveElement && !isDetailRow) {
        onRowClick(event.data);
      }
    },
    [onRowClick],
  );

  const handleSelectionChanged = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (onSelectionChanged) {
        onSelectionChanged(event);
      }
    },
    [onSelectionChanged],
  );

  const getRowClass = useCallback(
    (params: RowClassParams<T>) =>
      getRowClasses<T>({
        hoverable,
        onRowClick,
        onRowDoubleClick,
        striped: striped ?? false,
        params,
        isHighlighted,
      }),
    [hoverable, onRowClick, onRowDoubleClick, striped, isHighlighted],
  );

  const getRowIdFunc = useCallback(
    (params: GetRowIdParams<T>) => {
      if (getRowId) {
        return getRowId(params);
      }
      return String(params.data?.id);
    },
    [getRowId],
  );

  return {
    handleRowClick,
    handleSelectionChanged,
    getRowClass,
    getRowIdFunc,
  };
};
